import type {
  ActorId,
  AdminRecentActivityEntry,
  AdminChatInboxResponse,
  AdminChatConversationResponse,
  AdminChatOnlineRosterResponse,
  AdminSendChatMessageResponse,
  MetricsPanelDescriptor,
  ObservabilityBridgeLink,
  OrderId,
  ProviderId,
} from '@kopechka/contracts';
import {
  callAdminApi,
  resolveBaseUrl,
  type ApiResult,
  type ApiClientOptions,
} from './client.js';
import { freshIdempotencyKey } from './idempotency-key.js';
import type { LoginFailureReasonWire } from '../i18n/login-failure-reasons.js';

// ----- Wire types (Dates are ISO strings on the wire) -----

export interface WireMaintenanceState {
  readonly enabled: boolean;
  readonly scope: 'none' | 'orders_only' | 'customer_orchestration' | 'full';
  readonly reason: string | null;
  readonly updatedAt: string;
}

export interface MaintenanceToggleResponse {
  readonly status: 'applied' | 'idempotent_replay';
  readonly state: WireMaintenanceState;
  readonly correlationId: string;
}

export interface WireUserWallet {
  readonly currency: string;
  readonly balanceMinor: number;
  readonly updatedAt: string;
}

export interface WireUserOrderSummary {
  readonly orderId: string;
  readonly state: string;
  readonly sku: string | null;
  readonly providerId: string | null;
  readonly outcomeKind: 'delivered' | 'failed' | 'pending_review' | null;
  readonly openedAt: string;
  readonly updatedAt: string;
}

export interface WireUserView {
  readonly actorId: string;
  readonly displayName: string | null;
  readonly email: string | null;
  readonly moderationState: 'active' | 'banned' | 'soft_deleted';
  readonly bannedAt: string | null;
  readonly banReason: string | null;
  readonly deletedAt: string | null;
  readonly deleteReason: string | null;
  readonly wallets: readonly WireUserWallet[];
  readonly recentOrders: readonly WireUserOrderSummary[];
}

export interface WireDeliveryTimelineEntry {
  readonly eventId: number | null;
  readonly outcomeKind: 'delivered' | 'failed' | 'pending_review';
  readonly providerId: string;
  readonly failureReason: string | null;
  readonly providerMessageKey: string | null;
  readonly subject: string | null;
  readonly from: string | null;
  readonly detail: string | null;
  readonly recordedAt: string;
}

export interface WireOrderView {
  readonly orderId: string;
  readonly state: string;
  readonly reservationId: string | null;
  readonly sku: string | null;
  readonly providerId: string | null;
  readonly actorId: string | null;
  readonly correlationId: string;
  readonly openedAt: string;
  readonly updatedAt: string;
  readonly closedAt: string | null;
  readonly outcome: Record<string, unknown> | null;
  readonly deliveryTimeline: readonly WireDeliveryTimelineEntry[];
}

export interface WireProviderHealth {
  readonly providerId: string;
  readonly totalParents: number;
  readonly parentsByStatus: Readonly<Record<string, number>>;
  readonly aliasesByStatus: Readonly<Record<string, number>>;
  readonly sellableAliasEstimate: number;
  readonly degradationReason: string | null;
  readonly recentRenewalAttempts: number;
  readonly recentRenewalFailures: number;
  readonly observedAt: string;
  readonly maintenance: {
    readonly enabled: boolean;
    readonly scope: string;
    readonly reason: string | null;
  };
}

export interface WireActivityEntry extends Omit<AdminRecentActivityEntry, 'at'> {
  readonly at: string;
}

export interface WireObservabilityBridge {
  readonly baseUrl: string | null;
  readonly configured: boolean;
  readonly observedAt: string;
  readonly panels: readonly MetricsPanelDescriptor[];
  // Phase 19 — runtime-aware metadata.
  readonly metrics: readonly {
    readonly name: string;
    readonly kind: 'counter' | 'gauge' | 'histogram';
    readonly unit: 'count' | 'milliseconds' | 'bytes' | 'ratio' | 'timestamp';
    readonly category: string;
    readonly description: string;
    readonly requiredTags: readonly string[];
    readonly optionalTags: readonly string[];
    readonly consumers: readonly string[];
  }[];
  readonly slos: readonly {
    readonly sloId: string;
    readonly title: string;
    readonly description: string;
    readonly category: string;
    readonly objective: string;
    readonly target: {
      readonly target: number;
      readonly windowHours: number;
      readonly unit: 'ratio' | 'milliseconds' | 'count';
    };
    readonly sourceMetric: string;
    readonly owner: 'platform' | 'provider' | 'billing' | 'admin';
  }[];
  readonly alerts: readonly {
    readonly alertId: string;
    readonly title: string;
    readonly summary: string;
    readonly category: string;
    readonly severity: 'info' | 'warning' | 'critical' | 'page';
    readonly condition: 'gt_threshold' | 'lt_threshold' | 'burn_rate' | 'dead_man';
    readonly threshold: number;
    readonly windowMinutes: number;
    readonly sourceMetric: string;
    readonly runbookRefs: readonly string[];
    readonly routeTo: 'platform' | 'provider' | 'billing' | 'admin';
  }[];
  readonly alertSummaries: readonly {
    readonly alertId: string;
    readonly severity: 'info' | 'warning' | 'critical' | 'page';
    readonly state: 'ok' | 'warning' | 'firing' | 'unknown';
    readonly lastEvaluatedAt: string | null;
    readonly lastFiredAt: string | null;
    readonly detail: string | null;
  }[];
  readonly overallSeverity:
    | 'ok'
    | 'info'
    | 'warning'
    | 'critical'
    | 'page'
    | 'unknown';
}

// ----- Response envelopes -----

export interface UserInspectionResponse {
  readonly status: 'ok';
  readonly user: WireUserView;
  readonly correlationId: string;
}

export interface OrderInspectionResponse {
  readonly status: 'ok';
  readonly order: WireOrderView;
  readonly correlationId: string;
}

export interface ProviderHealthResponse {
  readonly status: 'ok';
  readonly provider: WireProviderHealth;
  readonly correlationId: string;
}

// R22.2 — V1-shape additions on /admin/activities/recent.
// `entries[]` stays (admin event projection, back-compat). New `data.*`
// surface includes recent customer signups, orders, transactions, and
// a pagination envelope.
export interface WireRecentSignup {
  readonly actorId: string;
  readonly email: string | null;
  readonly username: string | null;
  readonly displayName: string | null;
  readonly isActive: boolean;
  readonly createdAt: string;
}

export interface WireRecentOrderActivity {
  readonly type: 'order';
  readonly orderId: string;
  readonly actorId: string | null;
  readonly actor: string;
  readonly state: string;
  readonly providerId: string | null;
  readonly sku: string | null;
  readonly amountMinor: number | null;
  /**
   * R-ACTIVITY-MICRO — exact charge in micro (1e-6 USD; 4000 = $0.004). The
   * "Tutar" column renders this so sub-cent prices show honestly instead of
   * the cent-floored amountMinor ($0.01). Optional: older API builds omit it.
   */
  readonly amountMicro?: number | null;
  readonly currency: string;
  /**
   * R-ACTIVITY-V1UI — V1 RecentActivities "Site" column (site_label ??
   * site_key from the orders row). Optional: older API builds omit it.
   */
  readonly site?: string | null;
  /**
   * TRACK A — the alias DOMAIN the order targeted (mail.com / email.com /
   * usa.com / gmx.com), persisted on the orders row so it is present even
   * for CANCELLED orders. Optional / nullable: older API builds omit it and
   * pre-migration rows whose alias was already released carry null.
   */
  readonly domain?: string | null;
  /**
   * D-232 — inline "mail/code arrived?" signal for the order row. `codeReceived`
   * is true once a verification mail is saved to provider_inbox_messages;
   * `codeReceivedAt` is the first-mail time. State-agnostic (post-D-232 the
   * order sits 'reserved' for the full 10-min lease). Optional: older API builds
   * omit them (then the row shows "bekliyor").
   */
  readonly codeReceived?: boolean;
  readonly codeReceivedAt?: string | null;
  readonly createdAt: string;
}

export interface WireRecentTransactionActivity {
  readonly type: 'transaction';
  readonly id: string;
  readonly actorId: string;
  readonly actor: string;
  readonly transactionType: string;
  readonly amount: number;
  readonly currency: string;
  readonly status: string;
  readonly description: string | null;
  readonly createdAt: string;
}

export interface RecentActivityResponse {
  readonly status: 'ok';
  readonly entries: readonly WireActivityEntry[];
  readonly data?: {
    readonly recentUsers: readonly WireRecentSignup[];
    readonly recentOrders: readonly WireRecentOrderActivity[];
    readonly recentTransactions: readonly WireRecentTransactionActivity[];
    readonly pagination: {
      readonly page: number;
      readonly limit: number;
      readonly totalOrders: number;
      readonly hasMore: boolean;
    };
  };
  readonly correlationId: string;
}

export interface ObservabilityResponse {
  readonly status: 'ok';
  readonly bridge: WireObservabilityBridge;
  readonly correlationId: string;
}

export interface ModerationResponse {
  readonly status: string;
  readonly correlationId: string;
}

export interface BalanceAdjustmentResponse {
  readonly kind: 'applied' | 'idempotent_replay' | 'user_not_found' | 'invalid_amount' | 'invalid_currency';
  readonly correlationId: string;
  readonly adjustmentId?: string;
  readonly balanceMinor?: number;
  readonly currency?: string;
}

// ----- Query endpoints -----

export function getUserInspection(
  options: ApiClientOptions,
  actorId: ActorId | string,
): Promise<ApiResult<UserInspectionResponse>> {
  return callAdminApi(options, {
    method: 'GET',
    path: `/admin/users/${encodeURIComponent(String(actorId))}/orders`,
  });
}

export function getOrderInspection(
  options: ApiClientOptions,
  orderId: OrderId | string,
): Promise<ApiResult<OrderInspectionResponse>> {
  return callAdminApi(options, {
    method: 'GET',
    path: `/admin/orders/${encodeURIComponent(String(orderId))}/inspection`,
  });
}

export function getProviderHealth(
  options: ApiClientOptions,
  providerId: ProviderId | string,
): Promise<ApiResult<ProviderHealthResponse>> {
  return callAdminApi(options, {
    method: 'GET',
    path: `/admin/providers/${encodeURIComponent(String(providerId))}/health`,
  });
}

export function getRecentActivity(
  options: ApiClientOptions,
  limit = 50,
  page = 1,
): Promise<ApiResult<RecentActivityResponse>> {
  return callAdminApi(options, {
    method: 'GET',
    path:
      `/admin/activities/recent?limit=${encodeURIComponent(String(limit))}` +
      `&page=${encodeURIComponent(String(page))}`,
  });
}

export function getObservabilityBridge(
  options: ApiClientOptions,
  category?: string,
): Promise<ApiResult<ObservabilityResponse>> {
  const q = category ? `?category=${encodeURIComponent(category)}` : '';
  return callAdminApi(options, {
    method: 'GET',
    path: `/admin/observability/panels${q}`,
  });
}

// ----- Mutation endpoints -----

export interface ToggleMaintenanceInput {
  readonly enabled: boolean;
  readonly scope: 'none' | 'orders_only' | 'customer_orchestration' | 'full';
  readonly reason: string | null;
}

export function toggleMaintenance(
  options: ApiClientOptions,
  input: ToggleMaintenanceInput,
): Promise<ApiResult<MaintenanceToggleResponse>> {
  return callAdminApi(options, {
    method: 'POST',
    path: '/admin/maintenance-mode',
    body: input,
    idempotencyKey: freshIdempotencyKey('maintenance'),
  });
}

export interface ModerationInput {
  readonly actorId: string;
  readonly reason: string;
}

export function banUser(
  options: ApiClientOptions,
  input: ModerationInput,
): Promise<ApiResult<ModerationResponse>> {
  return callAdminApi(options, {
    method: 'POST',
    path: `/admin/users/${encodeURIComponent(input.actorId)}/ban`,
    body: { reason: input.reason },
    idempotencyKey: freshIdempotencyKey('ban'),
  });
}

export function unbanUser(
  options: ApiClientOptions,
  input: ModerationInput,
): Promise<ApiResult<ModerationResponse>> {
  return callAdminApi(options, {
    method: 'POST',
    path: `/admin/users/${encodeURIComponent(input.actorId)}/unban`,
    body: { reason: input.reason },
    idempotencyKey: freshIdempotencyKey('unban'),
  });
}

export function softDeleteUser(
  options: ApiClientOptions,
  input: ModerationInput,
): Promise<ApiResult<ModerationResponse>> {
  return callAdminApi(options, {
    method: 'POST',
    path: `/admin/users/${encodeURIComponent(input.actorId)}/delete`,
    body: { reason: input.reason },
    idempotencyKey: freshIdempotencyKey('delete'),
  });
}

export interface BalanceAdjustmentInput {
  readonly actorId: string;
  readonly amountMinor: number;
  readonly currency: string;
  readonly reason: string;
}

export function adjustBalance(
  options: ApiClientOptions,
  input: BalanceAdjustmentInput,
): Promise<ApiResult<BalanceAdjustmentResponse>> {
  return callAdminApi(options, {
    method: 'POST',
    path: `/admin/users/${encodeURIComponent(input.actorId)}/balance-adjustments`,
    body: {
      amountMinor: input.amountMinor,
      currency: input.currency,
      reason: input.reason,
    },
    idempotencyKey: freshIdempotencyKey('balance'),
  });
}

// ----- Domain pricing (admin) ----------------------------------------------

export interface WireDomainPriceRow {
  readonly id: string;
  readonly providerId: string;
  readonly domain: string;
  readonly unitPriceMinor: number;
  readonly unitPriceUsd: number;
  readonly currency: string;
  readonly status: string;
  readonly updatedBy: string | null;
  readonly effectiveFrom: string;
  readonly expiresAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  /** R-GMX-PARITY — display order from provider_pricing_catalog.display_order (lower = earlier on customer pages). */
  readonly displayOrder: number;
}

export interface DomainPricingListResponse {
  readonly rows: readonly WireDomainPriceRow[];
  readonly scaleNote: string;
}

export function getDomainPricing(
  options: ApiClientOptions,
): Promise<ApiResult<DomainPricingListResponse>> {
  return callAdminApi(options, { method: 'GET', path: '/admin/providers/pricing' });
}

export interface SetDomainPriceInput {
  readonly providerId: string;
  readonly domain: string;
  readonly unitPriceUsd: number;
  readonly currency?: string;
  readonly note?: string;
}

export function setDomainPrice(
  options: ApiClientOptions,
  input: SetDomainPriceInput,
): Promise<ApiResult<{ readonly outcome: string }>> {
  return callAdminApi(options, {
    method: 'PUT',
    path: `/admin/providers/${encodeURIComponent(input.providerId)}/domains/${encodeURIComponent(input.domain)}/pricing`,
    body: {
      unitPriceUsd: input.unitPriceUsd,
      currency: input.currency ?? 'USD',
      ...(input.note ? { note: input.note } : {}),
    },
    idempotencyKey: freshIdempotencyKey('pricing'),
  });
}

export interface SetDomainDisplayOrderInput {
  readonly providerId: string;
  readonly domain: string;
  readonly displayOrder: number;
}

/**
 * R-GMX-PARITY — set a domain's customer-page display order (lower = higher up).
 * Direct UPDATE on the active catalog row (display order is a display attribute,
 * not a priced fact, so it does NOT supersede a price-history row). Gated by the
 * same capability as price edit (pricing:shadow_update).
 */
export function setDomainDisplayOrder(
  options: ApiClientOptions,
  input: SetDomainDisplayOrderInput,
): Promise<ApiResult<WireDomainPriceRow>> {
  return callAdminApi(options, {
    method: 'PUT',
    path: `/admin/providers/${encodeURIComponent(input.providerId)}/domains/${encodeURIComponent(input.domain)}/display-order`,
    body: { displayOrder: input.displayOrder },
    idempotencyKey: freshIdempotencyKey('display-order'),
  });
}

// ----- Storefront settings (admin per-provider featured domain count) -------

export interface WireStorefrontSettingsRow {
  readonly providerId: string;
  readonly featuredCount: number;
  /**
   * Admin-editable customer-facing provider header label ("mail.com" / "GMX").
   * Null until seeded / set — the editor falls back to the known label or the
   * provider id, the customer surface to the hardcoded PROVIDER_LABELS map.
   */
  readonly displayLabel?: string | null;
  readonly updatedAt: string;
}

export interface StorefrontSettingsListResponse {
  readonly rows: readonly WireStorefrontSettingsRow[];
  readonly bounds: { readonly min: number; readonly max: number };
}

/**
 * TRACK B — list each provider's customer-storefront "featured domain count"
 * (the number of domains shown before the rest collapse behind a "show more").
 * Gated by the pricing-inspect capability (same authority as the pricing matrix).
 */
export function getStorefrontSettings(
  options: ApiClientOptions,
): Promise<ApiResult<StorefrontSettingsListResponse>> {
  return callAdminApi(options, {
    method: 'GET',
    path: '/admin/providers/storefront-settings',
  });
}

export interface SetStorefrontFeaturedCountInput {
  readonly providerId: string;
  readonly featuredCount: number;
}

export interface SetStorefrontFeaturedCountResponse {
  readonly outcome: 'updated';
  readonly row: WireStorefrontSettingsRow;
}

/**
 * TRACK B — set ONE provider's featured domain count. The backend validates
 * the value is an integer in [min, max] (floor is 1 — 0 is a silent no-op) and
 * persists it. Gated by the pricing-update capability (same as price edit).
 */
export function setStorefrontFeaturedCount(
  options: ApiClientOptions,
  input: SetStorefrontFeaturedCountInput,
): Promise<ApiResult<SetStorefrontFeaturedCountResponse>> {
  return callAdminApi(options, {
    method: 'PUT',
    path: `/admin/providers/${encodeURIComponent(input.providerId)}/storefront-settings`,
    body: { featuredCount: input.featuredCount },
    idempotencyKey: freshIdempotencyKey('storefront-featured'),
  });
}

export interface SetStorefrontDisplayLabelInput {
  readonly providerId: string;
  readonly displayLabel: string;
}

/**
 * Set ONE provider's customer-facing display label (the storefront header
 * "mail.com" / "GMX"). Hits the SAME storefront-settings PUT as the featured
 * count (the backend accepts either field). The backend trims + validates the
 * label is a non-empty string of at most 40 chars. Gated by the pricing-update
 * capability (same as price edit).
 */
export function setStorefrontDisplayLabel(
  options: ApiClientOptions,
  input: SetStorefrontDisplayLabelInput,
): Promise<ApiResult<SetStorefrontFeaturedCountResponse>> {
  return callAdminApi(options, {
    method: 'PUT',
    path: `/admin/providers/${encodeURIComponent(input.providerId)}/storefront-settings`,
    body: { displayLabel: input.displayLabel },
    idempotencyKey: freshIdempotencyKey('storefront-label'),
  });
}

// ----- Announcements (admin "Duyurular" authoring) -------------------------

export interface WireAdminAnnouncement {
  readonly id: string;
  readonly title: string;
  readonly body: string;
  readonly severity: 'info' | 'warning' | 'critical' | 'release' | 'maintenance';
  readonly actionUrl: string | null;
  readonly isActive: boolean;
  readonly publishedAt: string;
  readonly expiresAt: string | null;
  readonly createdBy: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface AdminAnnouncementListResponse {
  readonly rows: readonly WireAdminAnnouncement[];
}

export interface CreateAnnouncementBody {
  readonly title: string;
  readonly body: string;
  readonly severity?: string;
  readonly actionUrl?: string | null;
  readonly expiresAt?: string | null;
}

export function listAdminAnnouncements(
  options: ApiClientOptions,
): Promise<ApiResult<AdminAnnouncementListResponse>> {
  return callAdminApi(options, { method: 'GET', path: '/admin/announcements' });
}

export function createAdminAnnouncement(
  options: ApiClientOptions,
  input: CreateAnnouncementBody,
): Promise<ApiResult<{ announcement: WireAdminAnnouncement }>> {
  return callAdminApi(options, {
    method: 'POST',
    path: '/admin/announcements',
    body: input,
    idempotencyKey: freshIdempotencyKey('announcement-create'),
  });
}

export function updateAdminAnnouncement(
  options: ApiClientOptions,
  id: string,
  patch: Partial<CreateAnnouncementBody> & { readonly isActive?: boolean },
): Promise<ApiResult<{ announcement: WireAdminAnnouncement }>> {
  return callAdminApi(options, {
    method: 'PUT',
    path: `/admin/announcements/${encodeURIComponent(id)}`,
    body: patch,
    idempotencyKey: freshIdempotencyKey('announcement-update'),
  });
}

/**
 * HARD delete — permanently removes the announcement (and its per-user
 * read/dismiss rows) so it leaves the admin list entirely. The active/passive
 * toggle uses `updateAdminAnnouncement({ isActive })` instead.
 */
export function deleteAdminAnnouncement(
  options: ApiClientOptions,
  id: string,
): Promise<ApiResult<{ announcement: WireAdminAnnouncement }>> {
  return callAdminApi(options, {
    method: 'DELETE',
    path: `/admin/announcements/${encodeURIComponent(id)}`,
    idempotencyKey: freshIdempotencyKey('announcement-delete'),
  });
}

// ----- Support chat (D-231) ------------------------------------------------

export function getAdminChatInbox(
  options: ApiClientOptions,
): Promise<ApiResult<AdminChatInboxResponse>> {
  return callAdminApi(options, { method: 'GET', path: '/admin/chat/inbox' });
}

/** Active-now count (default) or the full online roster (?roster=1). */
export function getAdminChatOnline(
  options: ApiClientOptions,
  roster = false,
): Promise<ApiResult<AdminChatOnlineRosterResponse>> {
  return callAdminApi(options, {
    method: 'GET',
    path: `/admin/chat/online${roster ? '?roster=1' : ''}`,
  });
}

export function getAdminChatConversation(
  options: ApiClientOptions,
  actorId: string,
): Promise<ApiResult<AdminChatConversationResponse>> {
  return callAdminApi(options, {
    method: 'GET',
    path: `/admin/chat/conversations/${encodeURIComponent(actorId)}`,
  });
}

export function sendAdminChatMessage(
  options: ApiClientOptions,
  actorId: string,
  body: string,
): Promise<ApiResult<AdminSendChatMessageResponse>> {
  const idempotencyKey = freshIdempotencyKey('chat-admin-send');
  return callAdminApi(options, {
    method: 'POST',
    path: `/admin/chat/conversations/${encodeURIComponent(actorId)}/messages`,
    body: { body, idempotencyKey },
    idempotencyKey,
  });
}

export function markAdminChatRead(
  options: ApiClientOptions,
  actorId: string,
): Promise<ApiResult<{ success: true }>> {
  return callAdminApi(options, {
    method: 'POST',
    path: `/admin/chat/conversations/${encodeURIComponent(actorId)}/read`,
  });
}

// ----- Deposits (admin financial — V1 deposit-history parity) --------------

export interface WireTopupDeposit {
  readonly id: string;
  readonly actorId: string;
  readonly user: {
    readonly email: string | null;
    readonly username: string | null;
    readonly displayName: string | null;
  } | null;
  readonly amountMinor: number;
  readonly amountUsd: number;
  readonly currency: string;
  readonly methodId: string;
  readonly providerName: string | null;
  readonly providerIntentId: string | null;
  readonly redirectUrl: string | null;
  readonly status: string;
  readonly createdAt: string;
  readonly completedAt: string | null;
  readonly failedReason: string | null;
}

export interface AdminDepositsResponse {
  readonly status: 'ok';
  readonly entries: readonly WireTopupDeposit[];
  readonly total: number;
  readonly limit: number;
  readonly offset: number;
  readonly correlationId: string;
}

export function listAdminDeposits(
  options: ApiClientOptions,
  params: { status?: string; limit?: number; offset?: number } = {},
): Promise<ApiResult<AdminDepositsResponse>> {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.limit != null) q.set('limit', String(params.limit));
  if (params.offset != null) q.set('offset', String(params.offset));
  const qs = q.toString();
  return callAdminApi(options, {
    method: 'GET',
    path: `/admin/financial/deposits${qs ? `?${qs}` : ''}`,
  });
}

// ----- Financial resolution (Phase 18) -------------------------------------

export type WireReviewStatus = 'open' | 'resolved' | 'rejected' | 'escalated';
export type WireReviewSource =
  | 'reconciliation_mismatch'
  | 'payment_dispute'
  | 'refund_approval'
  | 'delivery_failure';
export type WireFinancialResolution =
  | 'refund_posted'
  | 'credit_issued'
  | 'adjustment_posted'
  | 'no_action'
  | 'rejected'
  | 'escalated';
export type WireRefundReason =
  | 'customer_request'
  | 'operator_goodwill'
  | 'chargeback_received'
  | 'duplicate_charge'
  | 'delivery_failure'
  | 'review_resolution';
export type WireRefundStatus = 'pending' | 'posted' | 'rejected' | 'failed';

export interface WireReviewCase {
  readonly reviewCaseId: string;
  readonly source: WireReviewSource;
  readonly status: WireReviewStatus;
  readonly checkoutIntentId: string | null;
  readonly orderId: string | null;
  readonly actorId: string | null;
  readonly correlationId: string;
  readonly reason: string;
  readonly detail: string | null;
  readonly openedAt: string;
  readonly resolvedAt: string | null;
  readonly resolvedByActorId: string | null;
  readonly resolution: WireFinancialResolution | null;
  readonly resolutionNote: string | null;
}

export interface WireRefundRequest {
  readonly refundRequestId: string;
  readonly checkoutIntentId: string;
  readonly orderId: string | null;
  readonly actorId: string;
  readonly amountMinor: number;
  readonly currency: string;
  readonly reason: WireRefundReason;
  readonly status: WireRefundStatus;
  readonly requestedByActorId: string;
  readonly requestedAt: string;
  readonly decidedByActorId: string | null;
  readonly decidedAt: string | null;
  readonly decision: 'approved' | 'rejected' | null;
  readonly ledgerEntryId: string | null;
  readonly failureReason: string | null;
  readonly reviewCaseId: string | null;
  readonly memo: string | null;
}

export interface FinancialReviewListResponse {
  readonly status: 'ok';
  readonly entries: readonly WireReviewCase[];
  readonly correlationId: string;
}

export interface FinancialReviewDetailResponse {
  readonly status: 'ok';
  readonly case: WireReviewCase;
  readonly refunds: readonly WireRefundRequest[];
  readonly financial: null | {
    readonly financialState: string;
    readonly paidAmountMinor: number;
    readonly refundedAmountMinor: number;
    readonly currency: string;
    readonly ledgerEntryIds: readonly string[];
    readonly providerName: string | null;
    readonly providerReference: string | null;
    readonly correlationId: string;
    readonly orderId: string | null;
    readonly observedAt: string;
  };
  readonly correlationId: string;
}

export interface FinancialReviewResolveResponse {
  readonly status: 'resolved';
  readonly case: WireReviewCase;
  readonly correlationId: string;
}

export interface RefundListResponse {
  readonly status: 'ok';
  readonly entries: readonly WireRefundRequest[];
  readonly correlationId: string;
}

export interface RefundPostResponse {
  readonly status: 'posted';
  readonly request: WireRefundRequest;
  readonly ledgerEntryId: string;
  readonly replay: boolean;
  readonly correlationId: string;
}

export function listFinancialReviews(
  options: ApiClientOptions,
  input: { readonly status?: WireReviewStatus; readonly source?: WireReviewSource; readonly limit?: number } = {},
): Promise<ApiResult<FinancialReviewListResponse>> {
  const q: string[] = [];
  if (input.status) q.push(`status=${encodeURIComponent(input.status)}`);
  if (input.source) q.push(`source=${encodeURIComponent(input.source)}`);
  if (input.limit) q.push(`limit=${encodeURIComponent(String(input.limit))}`);
  const qs = q.length > 0 ? `?${q.join('&')}` : '';
  return callAdminApi(options, {
    method: 'GET',
    path: `/admin/financial/reviews${qs}`,
  });
}

export function getFinancialReviewDetail(
  options: ApiClientOptions,
  reviewCaseId: string,
): Promise<ApiResult<FinancialReviewDetailResponse>> {
  return callAdminApi(options, {
    method: 'GET',
    path: `/admin/financial/reviews/${encodeURIComponent(reviewCaseId)}`,
  });
}

export interface ResolveReviewInput {
  readonly resolution: WireFinancialResolution;
  readonly resolutionNote: string | null;
  readonly refund?: {
    readonly amountMinor: number;
    readonly currency: string;
    readonly reason: WireRefundReason;
    readonly memo?: string | null;
  };
}

export function resolveFinancialReview(
  options: ApiClientOptions,
  reviewCaseId: string,
  input: ResolveReviewInput,
): Promise<ApiResult<FinancialReviewResolveResponse>> {
  return callAdminApi(options, {
    method: 'POST',
    path: `/admin/financial/reviews/${encodeURIComponent(reviewCaseId)}/resolve`,
    body: input,
    idempotencyKey: freshIdempotencyKey('review-resolve'),
  });
}

export interface DirectRefundInput {
  readonly checkoutIntentId: string;
  readonly amountMinor: number;
  readonly currency: string;
  readonly reason: WireRefundReason;
  readonly memo?: string | null;
}

export function postDirectRefund(
  options: ApiClientOptions,
  input: DirectRefundInput,
): Promise<ApiResult<RefundPostResponse>> {
  return callAdminApi(options, {
    method: 'POST',
    path: '/admin/refunds',
    body: input,
    idempotencyKey: freshIdempotencyKey('refund'),
  });
}

// ----- Phase 18 — Provider Control Plane endpoints -------------------------

export interface WireProviderAccount {
  readonly id: string;
  readonly accountHandle: string;
  readonly status: string;
  readonly sessionState: string;
  readonly lastSessionCheckedAt: string | null;
  readonly lastSuccessfulFetchAt: string | null;
  readonly consecutiveErrors: number;
  readonly createdAt: string;
  /**
   * Phase 35C — boolean presence only. The credential_ref string is never
   * returned to the admin UI; this flag drives the "Credential Ekle /
   * Güncelle" button on rows that need provisioning.
   */
  readonly hasCredential: boolean;
  // R-D214 — V1-parity status taxonomy. Optional / undefined when null
  // so older API responses (pre-migration 0041) remain valid.
  readonly statusDetail?: string | null;
  readonly suspendedReason?: string | null;
  readonly cooldownUntil?: string | null;
  readonly cooldownRemainingSec?: number | null;
  /**
   * 2026-09-22 — classified login-rejection reason {code, detail, at}. The
   * server sends it ONLY while status='login_failed' (the stored metadata is
   * stale history on recovered rows); null/absent otherwise and on older APIs.
   */
  readonly failureReason?: LoginFailureReasonWire | null;
}

export interface WireProviderAlias {
  readonly id: string;
  readonly domain: string;
  readonly status: string;
  readonly servedCount: number;
  readonly lastServedAt: string | null;
  readonly lastStatusChangedAt: string;
  readonly createdAt: string;
}

export interface WireInventoryProjection {
  readonly id: string;
  readonly accountId: string;
  readonly domain: string;
  readonly availableCount: number;
  readonly reservedCount: number;
  readonly leasedCount: number;
  readonly usedCount: number;
  readonly disabledCount: number;
  readonly totalImported: number;
  readonly lastRecomputedAt: string;
}

export interface WireRenewalJob {
  readonly id: string;
  readonly jobType: string;
  readonly aliasId: string | null;
  readonly status: string;
  readonly eligibility: string;
  readonly ineligibilityReason: string | null;
  readonly enqueuedAt: string | null;
  readonly startedAt: string | null;
  readonly completedAt: string | null;
  readonly attempts: number;
  readonly createdAt: string;
  /**
   * 2026-09-22 — last worker error for the job (server-truncated to 160
   * chars), e.g. the GMX phone-wall stage. Optional; older backends omit it.
   */
  readonly lastError?: string | null;
}

export interface WireProviderOutboxEvent {
  readonly id: string;
  readonly aggregateType: string;
  readonly aggregateId: string;
  readonly eventType: string;
  readonly status: string;
  readonly attempts: number;
  readonly correlationId: string | null;
  readonly createdAt: string;
  readonly publishedAt: string | null;
  readonly lastError: string | null;
}

export interface MailcomAccountsPagination {
  readonly page: number;
  readonly limit: number;
  readonly total: number;
  readonly pages: number;
}

export interface MailcomAccountsResponse {
  readonly providerId: string;
  readonly accounts: readonly WireProviderAccount[];
  // R22.2 D-042 — present when the request asked for pagination (page/limit/...)
  readonly pagination?: MailcomAccountsPagination;
  // R-D214 — V1-parity status taxonomy breakdown blocks. Optional; older
  // backends omit these.
  readonly statusBreakdown?: Readonly<Record<string, number>>;
  readonly statusDetailBreakdown?: Readonly<Record<string, number>>;
  readonly suspendedReasonBreakdown?: Readonly<Record<string, number>>;
  readonly cooldownActiveCount?: number;
  // FIX-3 (R-PARENT-CLASS) — transient count of renewal JOBS currently
  // running (provider_renewal_jobs.status='running'), the source for the
  // "Yenileniyor" card. Optional; older backends omit it.
  readonly runningRenewals?: number;
}

export interface MailcomAccountsQuery {
  readonly page?: number;
  readonly limit?: number;
  readonly search?: string;
  // R-D214 — 5-bucket status enum (added 'draining') + V1 taxonomy tokens
  // that the backend translates to status_detail / suspended_reason
  // filters. The UI sends the V1 token; backend partitions on the wire.
  readonly status?:
    | 'all'
    | 'active'
    | 'draining'
    | 'suspended'
    | 'login_failed'
    | 'disabled'
    | 'alias_limit_reached'
    | 'renewal_failed'
    | 'renewal_in_progress'
    | 'upstream_unavailable'
    | 'blocked'
    | 'bootstrapping'
    // 2026-09-22 — GMX phone-wall disabled parents (status_detail token).
    | 'phone_verification_required';
  readonly sortBy?: 'createdAt' | 'accountHandle' | 'status' | 'sessionState' | 'lastSuccessfulFetchAt';
  readonly sortOrder?: 'asc' | 'desc';
  // R-D214 — explicit V1-taxonomy filters; the UI's status dropdown
  // collapses these into the single `status` slot, but advanced
  // dashboards can drill via the explicit params.
  readonly statusDetail?: string;
  readonly suspendedReason?: string;
  readonly cooldownState?: 'active' | 'expired' | 'none';
}

export interface MailcomAccountCreateResponse {
  readonly id: string;
  readonly accountHandle: string;
  readonly status: string;
  readonly sessionState: string;
  readonly createdAt: string;
  /** Phase 20.4b — true when a credential ref was stored on the account. */
  readonly hasCredential?: boolean;
  /**
   * Phase 20.4b — 'stored' = backend wrote a vault: ref, 'reference' = the
   * caller passed an existing env: ref, 'absent' = no credential supplied.
   */
  readonly credentialRefStatus?: 'stored' | 'reference' | 'absent';
  /** Phase 20.4b — true when a refresh token (extraSecret) is on the credential. */
  readonly refreshTokenPresent?: boolean;
}

export interface MailcomAliasesResponse {
  readonly accountId: string;
  readonly total: number;
  readonly aliases: readonly WireProviderAlias[];
}

export interface MailcomImportAliasesResponse {
  readonly imported: number;
  readonly skipped: number;
}

export interface MailcomInventoryResponse {
  readonly providerId: string;
  readonly projections: readonly WireInventoryProjection[];
}

export interface MailcomRenewalsResponse {
  readonly accountId: string;
  readonly jobs: readonly WireRenewalJob[];
}

export interface MailcomEnqueueRenewalResponse {
  readonly jobId: string;
  readonly status: string;
}

export interface MailcomEventsResponse {
  readonly providerId: string;
  readonly events: readonly WireProviderOutboxEvent[];
}

// R-D214 — V1 taxonomy tokens that the backend reads from
// status_detail OR suspended_reason rather than the 5-bucket `status`
// column. The client splits the dropdown value: status-bucket tokens go
// on `status`, taxonomy tokens go on `statusDetail` / `suspendedReason`.
const V1_STATUS_BUCKETS = new Set([
  'all',
  'active',
  'draining',
  'suspended',
  'login_failed',
  'disabled',
]);
const V1_SUSPENDED_REASON_TOKENS = new Set([
  'alias_limit_reached',
  'upstream_unavailable',
  'blocked',
  // R-D217 FIX-2 (card-bug 1) — 'renewal_failed' is a PERSISTENT V1
  // suspended_reason token (adapter mark_account_renewal_failed writes
  // suspended_reason='renewal_failed'). Previously it fell through to the
  // statusDetail branch, so the "Yenileme Hatası" card (which counts
  // suspendedReasonBreakdown.renewal_failed) and the filter click targeted
  // DIFFERENT columns. Routing the filter onto suspendedReason makes the
  // card count and the filter target the same column.
  'renewal_failed',
]);
// Taxonomy tokens that live on provider_accounts.status_detail. Unknown tokens
// still fall through to statusDetail below; this set just names the known ones.
// 2026-09-22 — 'phone_verification_required' marks a GMX parent disabled at the
// non-skippable phone wall (status stays 'disabled', so it must NOT go on
// `status`).
const V1_STATUS_DETAIL_TOKENS = new Set([
  'renewal_in_progress',
  'bootstrapping',
  'phone_verification_required',
]);

export function listMailcomAccounts(
  options: ApiClientOptions,
  query: MailcomAccountsQuery = {},
  providerId: string = 'mailcom',
): Promise<ApiResult<MailcomAccountsResponse>> {
  const qs: string[] = [];
  if (query.page !== undefined) qs.push(`page=${encodeURIComponent(String(query.page))}`);
  if (query.limit !== undefined) qs.push(`limit=${encodeURIComponent(String(query.limit))}`);
  if (query.search) qs.push(`search=${encodeURIComponent(query.search)}`);
  if (query.status) {
    // R-D214: if the UI sent a V1 taxonomy token in `status`, re-route
    // it onto `statusDetail` / `suspendedReason` so the backend SELECT
    // hits the right column. Status-bucket values pass through unchanged.
    if (V1_STATUS_BUCKETS.has(query.status)) {
      qs.push(`status=${encodeURIComponent(query.status)}`);
    } else if (V1_SUSPENDED_REASON_TOKENS.has(query.status)) {
      qs.push(`suspendedReason=${encodeURIComponent(query.status)}`);
    } else if (V1_STATUS_DETAIL_TOKENS.has(query.status)) {
      qs.push(`statusDetail=${encodeURIComponent(query.status)}`);
    } else {
      qs.push(`statusDetail=${encodeURIComponent(query.status)}`);
    }
  }
  if (query.statusDetail) qs.push(`statusDetail=${encodeURIComponent(query.statusDetail)}`);
  if (query.suspendedReason) qs.push(`suspendedReason=${encodeURIComponent(query.suspendedReason)}`);
  if (query.cooldownState) qs.push(`cooldownState=${encodeURIComponent(query.cooldownState)}`);
  if (query.sortBy) qs.push(`sortBy=${encodeURIComponent(query.sortBy)}`);
  if (query.sortOrder) qs.push(`sortOrder=${encodeURIComponent(query.sortOrder)}`);
  const q = qs.length > 0 ? `?${qs.join('&')}` : '';
  return callAdminApi(options, {
    method: 'GET',
    path: `/admin/providers/${encodeURIComponent(providerId)}/accounts${q}`,
  });
}

/**
 * Phase 20.4b — provider-specific credential fields. Either:
 *   * parentBundle           = "email:password:refreshToken" (V1 parity)
 *   * parentEmail / parentPassword / parentRefreshToken (separate)
 *   * credentialRef          = pre-existing env: ref (advanced)
 *
 * The backend never returns raw credential material — only safe metadata.
 */
export interface AddMailcomAccountInput {
  readonly accountHandle: string;
  readonly parentBundle?: string | null;
  readonly parentEmail?: string | null;
  readonly parentPassword?: string | null;
  readonly parentRefreshToken?: string | null;
  readonly credentialRef?: string | null;
}

export function addMailcomAccount(
  options: ApiClientOptions,
  input: AddMailcomAccountInput,
  providerId: string = 'mailcom',
): Promise<ApiResult<MailcomAccountCreateResponse>> {
  return callAdminApi(options, {
    method: 'POST',
    path: `/admin/providers/${encodeURIComponent(providerId)}/accounts`,
    body: input,
    idempotencyKey: freshIdempotencyKey(`${providerId}-account`),
  });
}

/**
 * Phase 35C — UPDATE the credential_ref on an existing parent account.
 * Same body shape as add (bundle / separate fields / advanced ref). Returns
 * boolean+credentialRefStatus only; raw secret never crosses the wire.
 */
export interface UpdateMailcomAccountCredentialsInput {
  readonly accountId: string;
  readonly parentBundle?: string | null;
  readonly parentEmail?: string | null;
  readonly parentPassword?: string | null;
  readonly parentRefreshToken?: string | null;
  readonly credentialRef?: string | null;
}

export interface MailcomAccountCredentialUpdateResponse {
  readonly accountId: string;
  readonly accountHandle: string;
  readonly hasCredential: boolean;
  readonly credentialRefStatus: 'stored' | 'reference' | 'absent';
  readonly refreshTokenPresent: boolean;
}

export function updateMailcomAccountCredentials(
  options: ApiClientOptions,
  input: UpdateMailcomAccountCredentialsInput,
  providerId: string = 'mailcom',
): Promise<ApiResult<MailcomAccountCredentialUpdateResponse>> {
  const { accountId, ...body } = input;
  return callAdminApi(options, {
    method: 'POST',
    path: `/admin/providers/${encodeURIComponent(providerId)}/accounts/${encodeURIComponent(accountId)}/credentials`,
    body,
    idempotencyKey: freshIdempotencyKey(`${providerId}-account-credentials-${accountId}`),
  });
}

/**
 * 2026-06-06 — DECRYPT + reveal a parent's mail.com password for the operator
 * (click-to-reveal in the Parent Mail table). The backend decrypts via the
 * vault (AES-256-GCM, KOPECHKA_PROVIDER_CRED_KEY) and audits the ACCESS (never
 * the value). The password crosses the wire ONLY in this response — masked by
 * default in the UI. provider:manage_inventory gated. POST so it is never
 * cached / logged in a URL.
 */
export interface MailcomCredentialRevealResponse {
  readonly accountId: string;
  readonly accountHandle: string;
  readonly email: string;
  readonly password: string;
}

export function revealMailcomCredential(
  options: ApiClientOptions,
  accountId: string,
  providerId: string = 'mailcom',
): Promise<ApiResult<MailcomCredentialRevealResponse>> {
  return callAdminApi(options, {
    method: 'POST',
    path: `/admin/providers/${encodeURIComponent(providerId)}/parents/${encodeURIComponent(accountId)}/credentials/reveal`,
    body: {},
    idempotencyKey: freshIdempotencyKey(`${providerId}-credential-reveal-${accountId}`),
  });
}

export function listMailcomAliases(
  options: ApiClientOptions,
  accountId: string,
  providerId: string = 'mailcom',
): Promise<ApiResult<MailcomAliasesResponse>> {
  return callAdminApi(options, {
    method: 'GET',
    path: `/admin/providers/${encodeURIComponent(providerId)}/accounts/${encodeURIComponent(accountId)}/aliases`,
  });
}

export function importMailcomAliases(
  options: ApiClientOptions,
  accountId: string,
  aliasAddresses: readonly string[],
  providerId: string = 'mailcom',
): Promise<ApiResult<MailcomImportAliasesResponse>> {
  return callAdminApi(options, {
    method: 'POST',
    path: `/admin/providers/${encodeURIComponent(providerId)}/accounts/${encodeURIComponent(accountId)}/aliases/import`,
    body: { aliasAddresses },
    idempotencyKey: freshIdempotencyKey(`${providerId}-aliases-import-${accountId}`),
  });
}

export interface MailcomImportTxtResponse {
  readonly imported: number;
  readonly skipped: number;
  readonly errors: number;
  readonly errorDetails?: ReadonlyArray<{ line: number; error: string }>;
  readonly enqueueStatus: 'background';
}

/**
 * Phase N HOTFIX (V1 parity) — single-POST batch parent import. The whole TXT
 * file is sent as one `text` string; the backend parses + upserts every line
 * and fires bootstrap-renewal in the background. Replaces the per-row
 * addMailcomAccount loop. V1 ref: parentMailsAPI.importTxt (ParentMails.jsx:248).
 */
export function postMailcomImportTxt(
  options: ApiClientOptions,
  params: { readonly text: string; readonly aliasDomain?: string },
  providerId: string = 'mailcom',
): Promise<ApiResult<MailcomImportTxtResponse>> {
  return callAdminApi(options, {
    method: 'POST',
    path: `/admin/providers/${encodeURIComponent(providerId)}/import`,
    body: params,
    idempotencyKey: freshIdempotencyKey(`${providerId}-import-txt`),
  });
}

export interface MailcomBulkDeleteAndRenewResult {
  readonly dryRun?: boolean;
  readonly backgrounded?: boolean;
  readonly affectedAccountCount: number;
  readonly affectedAliasCount: number;
  // H3 FIX (2026-06-06) — leased/reserved aliases are PROTECTED (skipped), not
  // deleted. `activeOrdersImpacted` is kept as a back-compat alias of
  // `activeOrdersProtected` (same number, now meaning "will be protected").
  readonly activeOrdersImpacted: number;
  readonly activeOrdersProtected?: number;
  readonly byStatus?: Record<string, number>;
  readonly statuses: readonly string[];
}

/**
 * Phase N HOTFIX (V1 ParentMails.jsx bulk-delete-aliases parity). FORCE op:
 * wipe live aliases for parents in `statusFilter` + bootstrap-renew. 2-step:
 * confirm=false → dry-run counts; confirm=true → backgrounded wipe+renew.
 * provider:drain (super admin) — same gate as export-and-delete.
 */
export function postMailcomBulkDeleteAndRenew(
  options: ApiClientOptions,
  params: { readonly confirm: boolean; readonly statusFilter: readonly string[] },
  providerId: string = 'mailcom',
): Promise<ApiResult<MailcomBulkDeleteAndRenewResult>> {
  return callAdminApi(options, {
    method: 'POST',
    path: `/admin/providers/${encodeURIComponent(providerId)}/aliases/bulk-delete-and-renew`,
    body: params,
    idempotencyKey: freshIdempotencyKey(`${providerId}-bulk-delete-renew`),
  });
}

export interface MailcomBootstrapRenewalResponse {
  readonly jobId: string;
  readonly bypassedEligibility: boolean;
  readonly source: string;
  readonly workflowId?: string;
  readonly runId?: string;
  readonly status?: string;
}

/**
 * Phase N.IMPORT-BOOTSTRAP (D-146 KARAR-1) — start a bootstrap renewal for a
 * freshly imported parent. The workflow runs with mode:'bootstrap', bypassing
 * ONLY the G1/G2 ineligible gate so the (empty) parent's first aliases are
 * generated on mail.com. No request body.
 */
export function postMailcomBootstrapRenewal(
  options: ApiClientOptions,
  accountId: string,
  providerId: string = 'mailcom',
): Promise<ApiResult<MailcomBootstrapRenewalResponse>> {
  return callAdminApi(options, {
    method: 'POST',
    path: `/admin/providers/${encodeURIComponent(providerId)}/accounts/${encodeURIComponent(accountId)}/bootstrap-renewal`,
    idempotencyKey: freshIdempotencyKey(`${providerId}-bootstrap-renewal-${accountId}`),
  });
}

export function getMailcomInventory(
  options: ApiClientOptions,
  providerId: string = 'mailcom',
): Promise<ApiResult<MailcomInventoryResponse>> {
  return callAdminApi(options, {
    method: 'GET',
    path: `/admin/providers/${encodeURIComponent(providerId)}/inventory`,
  });
}

export function listMailcomRenewals(
  options: ApiClientOptions,
  accountId: string,
  providerId: string = 'mailcom',
): Promise<ApiResult<MailcomRenewalsResponse>> {
  return callAdminApi(options, {
    method: 'GET',
    path: `/admin/providers/${encodeURIComponent(providerId)}/accounts/${encodeURIComponent(accountId)}/renewals`,
  });
}

export function enqueueMailcomRenewal(
  options: ApiClientOptions,
  accountId: string,
  aliasId?: string | null,
  providerId: string = 'mailcom',
): Promise<ApiResult<MailcomEnqueueRenewalResponse>> {
  return callAdminApi(options, {
    method: 'POST',
    path: `/admin/providers/${encodeURIComponent(providerId)}/accounts/${encodeURIComponent(accountId)}/renewals`,
    body: { aliasId: aliasId ?? null },
    idempotencyKey: freshIdempotencyKey(`${providerId}-renewal-${accountId}`),
  });
}

export function listMailcomEvents(
  options: ApiClientOptions,
  limit = 50,
  providerId: string = 'mailcom',
): Promise<ApiResult<MailcomEventsResponse>> {
  return callAdminApi(options, {
    method: 'GET',
    path: `/admin/providers/${encodeURIComponent(providerId)}/events?limit=${encodeURIComponent(String(limit))}`,
  });
}

// ----- Phase 32 — operator overview + readiness ---------------------------

export interface MailcomSummaryResponse {
  readonly providerId: string;
  readonly accounts: {
    readonly total: number;
    readonly byStatus: Readonly<Record<string, number>>;
    readonly withCredential: number;
    readonly withoutCredential: number;
    /**
     * R-ALIASLIMIT-CARD — ACTIVE parents holding 1-8 live aliases (below the
     * 9-target but not empty). Feeds the "Alias Limiti" card directly from
     * live alias counts (operator-directed, count-based not status-based).
     * Optional: older API builds omit it.
     */
    readonly belowTarget?: number;
  };
  readonly aliases: {
    readonly available: number;
    readonly reserved: number;
    readonly leased: number;
    readonly used: number;
    readonly disabled: number;
    readonly totalImported: number;
    readonly domains: number;
  };
  /** R38 (D-153) — projection-sourced renewal breakdown ("Yenileniyor" card). */
  readonly renewal?: {
    readonly renewing: number;
  };
  readonly activity: {
    readonly lastSuccessfulFetchAt: string | null;
    readonly lastSessionCheckedAt: string | null;
  };
}

export interface MailcomReadinessResponse {
  readonly providerId: string;
  readonly ready: boolean;
  readonly accountsWithCredentialPresent: boolean;
  readonly availableAliasPresent: boolean;
  readonly credentialVaultConfigured: boolean;
  readonly oauthClientIdPresent: boolean;
  readonly oauthClientSecretPresent: boolean;
  readonly upstreamBaseUrlPresent: boolean;
  /**
   * Phase 37 — additional env presence. Booleans only; the env value itself
   * never crosses the wire. R38 (D-153) removed the V1-bridge account-id
   * reconciliation fields — the per-account vault is the V2 truth, so the
   * bridge env is no longer surfaced (see CREDENTIAL_PATH_DIAGNOSIS / D-149).
   */
  readonly oauthAuthMethodPresent?: boolean;
  readonly warnings: readonly string[];
}

export interface MailcomSessionRefreshResponse {
  readonly outcome: 'enqueued';
  readonly jobId: string;
  readonly kind: 'session_refresh';
}

export function getMailcomSummary(
  options: ApiClientOptions,
  providerId: string = 'mailcom',
): Promise<ApiResult<MailcomSummaryResponse>> {
  return callAdminApi(options, {
    method: 'GET',
    path: `/admin/providers/${encodeURIComponent(providerId)}/summary`,
  });
}

export function getMailcomReadiness(
  options: ApiClientOptions,
  providerId: string = 'mailcom',
): Promise<ApiResult<MailcomReadinessResponse>> {
  return callAdminApi(options, {
    method: 'GET',
    path: `/admin/providers/${encodeURIComponent(providerId)}/readiness`,
  });
}

export function refreshMailcomSession(
  options: ApiClientOptions,
  accountId: string,
  providerId: string = 'mailcom',
): Promise<ApiResult<MailcomSessionRefreshResponse>> {
  return callAdminApi(options, {
    method: 'POST',
    path: `/admin/providers/${encodeURIComponent(providerId)}/accounts/${encodeURIComponent(accountId)}/session-refresh`,
    body: {},
    idempotencyKey: freshIdempotencyKey(`${providerId}-session-refresh-${accountId}`),
  });
}

// ----- Phase 37 — bulk run-due renewals (Toplu Yenileme button) ------------

export interface MailcomRunDueResponse {
  readonly providerId: string;
  readonly totalDue: number;
  readonly temporalMode?: 'real' | 'deferred';
  readonly scheduled: readonly {
    readonly jobId: string;
    readonly accountId: string;
    readonly aliasId: string | null;
    readonly jobType: string;
    readonly workflowId: string;
    readonly workflowStarted: boolean;
    readonly temporalStartMode: 'real' | 'deferred' | 'already_running' | 'failed';
  }[];
}

export function runDueMailcomRenewals(
  options: ApiClientOptions,
  limit = 20,
  providerId: string = 'mailcom',
): Promise<ApiResult<MailcomRunDueResponse>> {
  return callAdminApi(options, {
    method: 'POST',
    path: `/admin/providers/${encodeURIComponent(providerId)}/renewals/run-due?limit=${encodeURIComponent(String(limit))}`,
    body: {},
    idempotencyKey: freshIdempotencyKey(`${providerId}-run-due`),
  });
}

// ---------------------------------------------------------------------------
// Phase 22 R22.2 — V1 deep parity for /admin/users surface (D-040)
//
// New list endpoint, deep user-detail endpoint (separate path from the
// legacy /admin/users/:id/orders slim view), and three V1 admin
// mutations (balance/discount/refund-status). API-key reveal returns
// only the safe prefix because V2 stores hash + prefix; regenerate
// emits a one-shot plain key.
// ---------------------------------------------------------------------------

/** Balance-tier system wire shapes (replaces the dead rank/custom_discount). */
export interface WireResolvedTier {
  readonly scope: 'lifetime' | 'weekly';
  readonly tierIndex: number | null;
  readonly name: string | null;
  readonly discountBps: number;
  readonly source: 'auto' | 'override' | 'none';
  readonly currentTotalMicro: number;
  readonly nextTierIndex: number | null;
  readonly nextTierName: string | null;
  readonly nextThresholdMicro: number | null;
}

export interface WireUserTierView {
  readonly lifetime: WireResolvedTier;
  readonly weekly: WireResolvedTier;
  readonly effectiveDiscountBps: number;
  readonly effectiveScope: 'lifetime' | 'weekly' | null;
  readonly lifetimeTotalMicro: number;
  readonly lifetimeTopupCount: number;
  readonly weekTotalMicro: number;
  readonly weekTopupCount: number;
  readonly weekKey: string;
  readonly overrideLifetimeTier: number | null;
  readonly overrideWeeklyTier: number | null;
}

export interface WireAdminUserListEntry {
  readonly presenceAvailable?: boolean;
  readonly lastActiveSource?: 'activity' | 'last_login' | 'unknown';
  readonly actorId: string;
  readonly email: string | null;
  readonly username: string | null;
  readonly displayName: string | null;
  readonly role: string;
  readonly isActive: boolean;
  readonly emailVerified: boolean;
  readonly isBanned: boolean;
  readonly bannedAt: string | null;
  readonly banReason: string | null;
  readonly isDeleted: boolean;
  readonly refundDisabled: boolean;
  readonly tier: WireUserTierView;
  readonly balance: number; // USD whole-dollar amount
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly lastLogin: string | null;
  // D-231 F6 — real-activity presence.
  readonly lastActiveAt: string | null;
  readonly isOnlineNow: boolean;
}

export interface AdminUserListResponse {
  readonly status: 'ok';
  readonly data: {
    readonly users: readonly WireAdminUserListEntry[];
    readonly total: number;
    readonly currentPage: number;
    readonly totalPages: number;
  };
  readonly correlationId: string;
}

export interface WireAdminUserDetailOrder {
  readonly orderId: string;
  readonly state: string;
  readonly sku: string | null;
  readonly providerId: string | null;
  readonly openedAt: string;
  readonly updatedAt: string;
  readonly outcome: Record<string, unknown> | null;
  readonly finalPriceMinor: number | null;
  /** D-232 — inline "mail/code arrived?" + first-mail time. Optional: older API builds omit. */
  readonly codeReceived?: boolean;
  readonly codeReceivedAt?: string | null;
  /**
   * Which SITE the mail was bought for, and which mailbox served it. Both were
   * missing from this table, which made a customer's order history unreadable:
   * every row looked identical. Optional — older API builds omit them.
   */
  readonly siteKey?: string | null;
  readonly siteLabel?: string | null;
  readonly domain?: string | null;
  readonly mailboxAddress?: string | null;
  /**
   * Exact charge in MICRO (1e-6 USD). `finalPriceMinor` came from
   * checkout_intents, which is empty for balance-paid orders — that is why the
   * amount column rendered "—" for every row. This comes from ledger_entries.
   */
  readonly chargedMicro?: number | null;
  readonly refundedMicro?: number | null;
}

export interface WireAdminUserDetailTransaction {
  readonly id: string;
  readonly type: string;
  readonly amount: number;
  readonly currency: string;
  readonly status: string;
  readonly description: string | null;
  readonly relatedOrderId: string | null;
  readonly balanceAfter: number | null;
  readonly createdAt: string;
}

export interface WireAdminUserDetailApiKey {
  readonly id: string;
  readonly label: string | null;
  readonly prefix: string;
  readonly createdAt: string;
  readonly lastUsedAt: string | null;
  readonly revokedAt: string | null;
}

/**
 * Telegram channel-reward link of an account (contract C2, D-235).
 * `linkedVia`: 'site' = one-time deep link from the customer panel,
 * 'bot_email' = bot-first flow (e-mail + 6-digit code). `member: null` means
 * the channel membership has not been checked (or the check failed).
 * The reward claim survives an admin unlink, so a Telegram id is never
 * rewarded twice.
 */
export interface WireAdminUserTelegram {
  readonly userId: string;
  readonly username: string | null;
  readonly linkedAt: string;
  readonly linkedVia: 'site' | 'bot_email';
  readonly member: boolean | null;
  readonly memberCheckedAt: string | null;
  readonly reward: {
    readonly state: 'none' | 'granted' | 'revoked';
    readonly grantedAt: string | null;
    readonly revokedAt: string | null;
    readonly balanceWentNegative: boolean;
  };
}

/**
 * O4 (2026-09-25) — the open-order cap behind POST /api/v1/orders' 429
 * too_many_pending. `default` = CUSTOMER_MAX_CONCURRENT_ORDERS (0 = no cap);
 * `override` = the account's raised cap (null = none); `effective` is the
 * server's max(override, default) — the UI shows it, never recomputes it.
 * `available: false` = the override store could not be read (e.g. migration
 * 0078 not applied): the override is unknown. `openOrders` = the account's
 * open orders right now (null = not readable); only on the detail GET.
 */
export interface WireAdminOpenOrderLimit {
  readonly override: number | null;
  readonly default: number;
  readonly effective: number;
  readonly available: boolean;
  readonly openOrders?: number | null;
}

export interface WireAdminUserDetail {
  /**
   * Telegram link (C2). `null` = not linked; absent (undefined) = an API
   * build that predates the field. See also AdminUserDetailResponse.data.telegram.
   */
  readonly telegram?: WireAdminUserTelegram | null;
  readonly presenceAvailable?: boolean;
  readonly lastActiveSource?: 'activity' | 'last_login' | 'unknown';
  readonly actorId: string;
  readonly email: string | null;
  readonly username: string | null;
  readonly displayName: string | null;
  readonly profile: {
    readonly firstName: string | null;
    readonly lastName: string | null;
    readonly timezone: string | null;
    readonly gender: string | null;
  };
  readonly balance: number;
  readonly wallets: readonly { readonly currency: string; readonly balanceMinor: number }[];
  readonly tier: WireUserTierView;
  readonly role: string;
  readonly isActive: boolean;
  readonly emailVerified: boolean;
  readonly isBanned: boolean;
  readonly banReason: string | null;
  readonly bannedAt: string | null;
  readonly isDeleted: boolean;
  readonly deleteReason: string | null;
  readonly deletedAt: string | null;
  readonly refundDisabled: boolean;
  /**
   * O4 (2026-09-25) — per-account open-order cap (admin-only). Absent =
   * an API build that predates the field (shown as unknown).
   */
  readonly openOrderLimit?: WireAdminOpenOrderLimit;
  readonly loginHistory: readonly { readonly ip: string; readonly timestamp: string; readonly userAgent: string }[];
  readonly referralCode: string | null;
  readonly referralEarnings: number;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly lastLogin: string | null;
  // D-231 F6 — real-activity presence.
  readonly lastActiveAt: string | null;
  readonly isOnlineNow: boolean;
}

export interface AdminUserDetailResponse {
  readonly status: 'ok';
  readonly data: {
    readonly user: WireAdminUserDetail;
    readonly orders: readonly WireAdminUserDetailOrder[];
    readonly transactions: readonly WireAdminUserDetailTransaction[];
    readonly totalOrdersCount: number;
    readonly completedOrdersCount: number;
    readonly apiKeys: readonly WireAdminUserDetailApiKey[];
    /**
     * Top-up history summary (ledger credits). "When did this account last put
     * money in?" was not answerable anywhere on the admin surface.
     * Optional — older API builds omit it.
     */
    readonly topups?: {
      readonly lastAt: string | null;
      readonly lastAmountMicro: number | null;
      readonly lastProvider: string | null;
      readonly count: number;
      readonly totalMicro: number;
      readonly firstAt: string | null;
    };
    /**
     * Telegram link (C2) when the API sends it next to `user` rather than
     * inside it. The page reads `user.telegram` first, then this.
     */
    readonly telegram?: WireAdminUserTelegram | null;
  };
  readonly correlationId: string;
}

export interface AdminUserListQuery {
  readonly page?: number;
  readonly limit?: number;
  readonly search?: string;
  readonly status?: 'all' | 'active' | 'banned' | 'deleted';
  readonly role?: 'all' | 'user' | 'admin' | 'superadmin' | 'support_readonly';
  readonly sortBy?: 'createdAt' | 'updatedAt' | 'email' | 'username' | 'balance' | 'isOnlineNow';
  readonly sortOrder?: 'asc' | 'desc';
}

export function listAdminUsers(
  options: ApiClientOptions,
  query: AdminUserListQuery = {},
): Promise<ApiResult<AdminUserListResponse>> {
  const qs: string[] = [];
  if (query.page !== undefined) qs.push(`page=${encodeURIComponent(String(query.page))}`);
  if (query.limit !== undefined) qs.push(`limit=${encodeURIComponent(String(query.limit))}`);
  if (query.search) qs.push(`search=${encodeURIComponent(query.search)}`);
  if (query.status) qs.push(`status=${encodeURIComponent(query.status)}`);
  if (query.role) qs.push(`role=${encodeURIComponent(query.role)}`);
  if (query.sortBy) qs.push(`sortBy=${encodeURIComponent(query.sortBy)}`);
  if (query.sortOrder) qs.push(`sortOrder=${encodeURIComponent(query.sortOrder)}`);
  const q = qs.length > 0 ? `?${qs.join('&')}` : '';
  return callAdminApi(options, { method: 'GET', path: `/admin/users${q}` });
}

export function getAdminUserDetail(
  options: ApiClientOptions,
  actorId: ActorId | string,
): Promise<ApiResult<AdminUserDetailResponse>> {
  return callAdminApi(options, {
    method: 'GET',
    path: `/admin/users/${encodeURIComponent(String(actorId))}/detail`,
  });
}

export interface AdminBalanceEditInput {
  readonly amount: number; // dollar units; backend converts to minor
  readonly type: 'add' | 'subtract' | 'set';
  readonly reason: string;
}

export interface AdminBalanceEditResponse {
  readonly status: string;
  readonly data: {
    readonly user: {
      readonly actorId: string;
      readonly balanceBefore: number;
      readonly balanceAfter: number;
      readonly difference: number;
    };
    readonly transaction: {
      readonly amount: number;
      readonly type: string;
      readonly description: string;
    };
  };
  readonly correlationId: string;
}

export function editAdminUserBalance(
  options: ApiClientOptions,
  actorId: ActorId | string,
  input: AdminBalanceEditInput,
): Promise<ApiResult<AdminBalanceEditResponse>> {
  return callAdminApi(options, {
    method: 'PUT',
    path: `/admin/users/${encodeURIComponent(String(actorId))}/balance`,
    body: input,
  });
}

// ----- Balance-tier: per-user manual override (replaces custom_discount) -----

export interface AdminTierOverrideResponse {
  readonly status: 'ok';
  readonly data: { readonly user: { readonly actorId: string; readonly tier: WireUserTierView } };
  readonly correlationId: string;
}

export function setAdminUserTierOverride(
  options: ApiClientOptions,
  actorId: ActorId | string,
  override: {
    readonly overrideLifetimeTier: number | null;
    readonly overrideWeeklyTier: number | null;
  },
): Promise<ApiResult<AdminTierOverrideResponse>> {
  return callAdminApi(options, {
    method: 'PUT',
    path: `/admin/users/${encodeURIComponent(String(actorId))}/tier-override`,
    body: override,
  });
}

// ----- Balance-tier: global config (Lifetime/Weekly ladders + weekly period) -

export interface WireTierDefinition {
  readonly scope: 'lifetime' | 'weekly';
  readonly tierIndex: number;
  readonly name: string;
  readonly minTopupMicro: number;
  readonly discountBps: number;
  readonly isActive: boolean;
  readonly description: string;
}

export interface WireWeeklyConfig {
  readonly startDow: number;
  readonly timezone: string;
}

export interface AdminTierConfigResponse {
  readonly tiers: readonly WireTierDefinition[];
  readonly weekly: WireWeeklyConfig;
}

export function getAdminTierConfig(
  options: ApiClientOptions,
): Promise<ApiResult<AdminTierConfigResponse>> {
  return callAdminApi(options, { method: 'GET', path: '/admin/tier-config' });
}

export function upsertAdminTierDefinition(
  options: ApiClientOptions,
  scope: 'lifetime' | 'weekly',
  tierIndex: number,
  // Discount rate is operator-FIXED (tier×5%) and the description is derived on
  // the customer page — neither is sent from admin; the route ignores them.
  body: {
    readonly name: string;
    readonly minTopupMicro: number;
    readonly isActive: boolean;
  },
): Promise<ApiResult<{ tier: WireTierDefinition }>> {
  return callAdminApi(options, {
    method: 'PUT',
    path: `/admin/tier-config/tiers/${scope}/${tierIndex}`,
    body,
  });
}

export function updateAdminWeeklyConfig(
  options: ApiClientOptions,
  body: { readonly startDow: number; readonly timezone: string },
): Promise<ApiResult<{ weekly: WireWeeklyConfig }>> {
  return callAdminApi(options, {
    method: 'PUT',
    path: '/admin/tier-config/weekly',
    body,
  });
}

export interface AdminRefundStatusResponse {
  readonly status: 'ok';
  readonly data: { readonly user: { readonly actorId: string; readonly refundDisabled: boolean } };
  readonly correlationId: string;
}

export function setAdminUserRefundStatus(
  options: ApiClientOptions,
  actorId: ActorId | string,
  disabled: boolean,
): Promise<ApiResult<AdminRefundStatusResponse>> {
  return callAdminApi(options, {
    method: 'PUT',
    path: `/admin/users/${encodeURIComponent(String(actorId))}/refund-status`,
    body: { disabled },
  });
}

/** O4 — PUT /admin/users/:id/open-order-limit (capability user:adjust_balance, audited). */
export interface AdminOpenOrderLimitResponse {
  readonly status: 'ok';
  readonly data: {
    readonly user: { readonly actorId: string; readonly openOrderLimit: WireAdminOpenOrderLimit };
  };
  readonly correlationId: string;
}

/**
 * O4 — set (`limit`: an integer above the default, at most 10000) or clear
 * (`limit: null`) the account's raised open-order cap. `reason` is an
 * optional audit note. Error reasons: limit_required, limit_out_of_range,
 * limit_not_above_default, default_cap_disabled, user_unknown,
 * limit_store_unavailable (403 capability_missing arrives as `forbidden`).
 */
export function setAdminUserOpenOrderLimit(
  options: ApiClientOptions,
  actorId: ActorId | string,
  body: { readonly limit: number | null; readonly reason?: string },
): Promise<ApiResult<AdminOpenOrderLimitResponse>> {
  return callAdminApi(options, {
    method: 'PUT',
    path: `/admin/users/${encodeURIComponent(String(actorId))}/open-order-limit`,
    body,
  });
}

export interface AdminApiKeyRevealResponse {
  readonly status: 'ok';
  readonly data: {
    readonly hasApiKey: boolean;
    readonly apiKey: string | null; // always null post-creation (hash-only V2)
    readonly prefix: string | null;
    readonly id: string | null;
    readonly label: string | null;
    readonly createdAt: string | null;
    readonly lastUsedAt: string | null;
  };
  readonly correlationId: string;
}

export function revealAdminUserApiKey(
  options: ApiClientOptions,
  actorId: ActorId | string,
): Promise<ApiResult<AdminApiKeyRevealResponse>> {
  return callAdminApi(options, {
    method: 'GET',
    path: `/admin/users/${encodeURIComponent(String(actorId))}/api-key`,
  });
}

export interface AdminApiKeyRegenerateResponse {
  readonly status: 'ok';
  readonly data: {
    readonly apiKey: string; // one-shot plain key
    readonly id: string;
    readonly prefix: string;
    readonly label: string | null;
    readonly note: string;
  };
  readonly correlationId: string;
}

export function regenerateAdminUserApiKey(
  options: ApiClientOptions,
  actorId: ActorId | string,
  label?: string | null,
): Promise<ApiResult<AdminApiKeyRegenerateResponse>> {
  return callAdminApi(options, {
    method: 'POST',
    path: `/admin/users/${encodeURIComponent(String(actorId))}/api-key/regenerate`,
    body: { label: label ?? null },
  });
}

export interface AdminApiKeyRevokeResponse {
  readonly status: 'ok';
  readonly data: { readonly revokedCount: number; readonly revokedIds: readonly string[] };
  readonly correlationId: string;
}

export function revokeAdminUserApiKey(
  options: ApiClientOptions,
  actorId: ActorId | string,
): Promise<ApiResult<AdminApiKeyRevokeResponse>> {
  return callAdminApi(options, {
    method: 'DELETE',
    path: `/admin/users/${encodeURIComponent(String(actorId))}/api-key`,
  });
}

// ----- Telegram channel reward: admin unlink (contract C2) -----------------

export interface AdminUserTelegramUnlinkResponse {
  readonly ok: true;
}

/** Contract error codes (`{ error: 'not_linked' }`) are snake_case; framework 404s are not. */
const CONTRACT_ERROR_CODE = /^[a-z][a-z0-9_]*$/;

/**
 * POST /admin/users/:actorId/telegram/unlink { reason } — capability
 * user:adjust_balance, audited server-side. The reward claim row is kept, so
 * the unlinked Telegram id can never be rewarded again.
 */
export async function postUserTelegramUnlink(
  options: ApiClientOptions,
  actorId: ActorId | string,
  reason: string,
): Promise<ApiResult<AdminUserTelegramUnlinkResponse>> {
  const res = await callAdminApi<AdminUserTelegramUnlinkResponse>(options, {
    method: 'POST',
    path: `/admin/users/${encodeURIComponent(String(actorId))}/telegram/unlink`,
    body: { reason },
    idempotencyKey: freshIdempotencyKey('telegram-unlink'),
  });
  // C2 answers `404 { error: 'not_linked' }` without a `reason` key, which the
  // shared client flattens to `http_404`; surface the contract code instead.
  if (res.kind === 'error' && res.reason === `http_${res.status}`) {
    const code = res.body?.error;
    if (typeof code === 'string' && CONTRACT_ERROR_CODE.test(code)) return { ...res, reason: code };
  }
  return res;
}

// ---------------------------------------------------------------------------
// Phase 22 R22.1 — alerts + webhook failure counter surface
//
// admin-ops-routes.ts'in 36 route'undan operatör tarafından kullanılmaya devam
// eden iki tanesi: alerts özet ve ödeme webhook failure sayaçları. Bu
// endpoint'leri provider/diagnostics/incident kompleksinden ayırıp burada
// minimal binding olarak tutuyoruz; geri kalan ops surface'ı R22.1'de silindi
// (D-038).
// ---------------------------------------------------------------------------

export interface WireAlertSummary {
  readonly overallSeverity:
    | 'ok'
    | 'info'
    | 'warning'
    | 'critical'
    | 'page'
    | 'unknown';
  readonly firingCount: number;
  readonly warningCount: number;
  readonly pageCount: number;
  readonly criticalCount: number;
  readonly okCount: number;
  readonly unknownCount: number;
  readonly recentCritical: readonly {
    readonly alertId: string;
    readonly title: string;
    readonly severity: 'info' | 'warning' | 'critical' | 'page';
    readonly state: 'ok' | 'warning' | 'firing' | 'unknown';
    readonly firedAt: string | null;
    readonly detail: string | null;
    readonly runbookRefs: readonly string[];
  }[];
  readonly providerId: string | null;
  readonly observedAt: string;
}

export interface AlertsSummaryResponse {
  readonly status: 'ok';
  readonly summary: WireAlertSummary;
  readonly correlationId: string;
}

export interface WebhookFailureCountsResponse {
  readonly status: 'ok';
  readonly counts: {
    readonly signatureRejected: number;
    readonly malformed: number;
    readonly duplicate: number;
    readonly intentUnknown: number;
    readonly applied: number;
    readonly total: number;
    readonly failureTotal: number;
    readonly since: string;
    readonly observedAt: string;
  };
  readonly correlationId: string;
}

export function getAlertsSummary(
  options: ApiClientOptions,
  providerId?: ProviderId | string,
): Promise<ApiResult<AlertsSummaryResponse>> {
  const qs = providerId
    ? `?providerId=${encodeURIComponent(String(providerId))}`
    : '';
  return callAdminApi(options, {
    method: 'GET',
    path: `/admin/alerts/summary${qs}`,
  });
}

export function getWebhookFailureCounts(
  options: ApiClientOptions,
): Promise<ApiResult<WebhookFailureCountsResponse>> {
  return callAdminApi(options, {
    method: 'GET',
    path: '/admin/payment/webhook-failure-counts',
  });
}

// ---------------------------------------------------------------------------
// R23.5 — V1 admin operator buttons (5 buton) ported to V2.
// ---------------------------------------------------------------------------

export interface MailcomBulkRenewResponse {
  readonly dryRun?: boolean;
  readonly totalCandidates: number;
  readonly started?: number;
  readonly deduplicated?: number;
  readonly enqueueRejected?: number;
  readonly workflowFailed?: number;
  readonly bucket?: string;
  readonly samples?: readonly { accountId: string; outcome: string; jobId?: string; reason?: string }[];
  // 2026-06-06 — FORCE wipe+re-add counters (undefined on non-force calls).
  readonly force?: boolean;
  readonly wipedAvailable?: number;
  readonly wipedUsed?: number;
  readonly protectedActiveOrders?: number;
}

export function postMailcomBulkRenew(
  options: ApiClientOptions,
  body: {
    confirm?: boolean;
    onlyDepleted?: boolean;
    // 2026-05-28 — opsiyonel. Verilmezse backend default ('active') uygular.
    statusFilter?: readonly string[];
    limit?: number;
    // 2026-06-06 — operator FORCE wipe+re-add. When true the backend
    // soft-deletes each parent's available+used aliases (protecting
    // leased/reserved) then re-renews so 9 fresh aliases are minted. Requires
    // provider:drain (destructive — burns the mail.com CAP-B budget).
    force?: boolean;
  },
  providerId: string = 'mailcom',
): Promise<ApiResult<MailcomBulkRenewResponse>> {
  return callAdminApi(options, {
    method: 'POST',
    path: `/admin/providers/${encodeURIComponent(providerId)}/parents/bulk-renew`,
    body,
    idempotencyKey: freshIdempotencyKey(`${providerId}-bulk-renew`),
  });
}

export interface MailcomReviveResponse {
  readonly dryRun?: boolean;
  readonly totalCandidates: number;
  readonly revivedStatus?: number;
  readonly outboxEnqueued?: number;
  readonly outboxIdempotentReplays?: number;
}

export function postMailcomRevive(
  options: ApiClientOptions,
  body: { confirm?: boolean; filterStatus?: 'disabled' | 'login_failed'; limit?: number },
  providerId: string = 'mailcom',
): Promise<ApiResult<MailcomReviveResponse>> {
  return callAdminApi(options, {
    method: 'POST',
    path: `/admin/providers/${encodeURIComponent(providerId)}/parents/revive`,
    body,
    idempotencyKey: freshIdempotencyKey(`${providerId}-revive`),
  });
}

export interface MailcomRetryFailedResponse {
  readonly dryRun?: boolean;
  readonly totalStaleJobs: number;
  readonly requeued?: number;
  readonly started?: number;
  readonly deduplicated?: number;
  readonly workflowFailed?: number;
  readonly byStatus?: Record<string, number>;
}

export function postMailcomRetryFailed(
  options: ApiClientOptions,
  body: { confirm?: boolean; olderThanHours?: number; limit?: number },
  providerId: string = 'mailcom',
): Promise<ApiResult<MailcomRetryFailedResponse>> {
  return callAdminApi(options, {
    method: 'POST',
    path: `/admin/providers/${encodeURIComponent(providerId)}/parents/retry-failed-renewals`,
    body,
    idempotencyKey: freshIdempotencyKey(`${providerId}-retry-failed`),
  });
}

export interface MailcomForceRenewResponse {
  // 2026-06-18 — optional: the provider-AGNOSTIC gmx force-renew (generic routes)
  // does NOT start a Temporal workflow (the Python pool claims the enqueued job
  // by provider_id), so its response carries no workflowId/runId. mailcom still
  // returns both. The admin toast reads only jobId/status/wiped* counters.
  readonly workflowId?: string;
  readonly runId?: string;
  readonly jobId: string;
  readonly status: 'started' | 'already_running';
  // 2026-06-06 — FORCE wipe+re-add counters (per-account).
  readonly wipedAvailable?: number;
  readonly wipedUsed?: number;
  readonly protectedActiveOrders?: number;
}

export function postMailcomForceRenew(
  options: ApiClientOptions,
  accountId: string,
  body: { reason: string; confirmationToken: string },
  providerId: string = 'mailcom',
): Promise<ApiResult<MailcomForceRenewResponse>> {
  return callAdminApi(options, {
    method: 'POST',
    path: `/admin/providers/${encodeURIComponent(providerId)}/parents/${encodeURIComponent(accountId)}/force-renew`,
    body,
    idempotencyKey: freshIdempotencyKey(`${providerId}-force-renew-${accountId}`),
  });
}

export interface MailcomExportPrepareResponse {
  readonly exportId: string;
  readonly fileName: string;
  readonly parentCount: number;
  readonly aliasCount: number;
  readonly csvBase64: string;
  readonly confirmationTokenHint: string;
}

export function postMailcomExportAndDelete(
  options: ApiClientOptions,
  // R36 (D-150) — V1 parity: `status` selects ALL parents of that status
  // (login_failed | disabled). `accountIds` is the legacy explicit-list path
  // (backward-compatible). Exactly one of the two should be provided.
  body: { accountIds?: string[]; status?: string; reason: string },
  providerId: string = 'mailcom',
): Promise<ApiResult<MailcomExportPrepareResponse>> {
  return callAdminApi(options, {
    method: 'POST',
    path: `/admin/providers/${encodeURIComponent(providerId)}/parents/export-and-delete`,
    body,
    idempotencyKey: freshIdempotencyKey(`${providerId}-export-and-delete`),
  });
}

export interface MailcomExportConfirmResponse {
  readonly exportId: string;
  readonly confirmedAt: string;
  readonly deletedAccounts: number;
  // 2026-05-28 hard-delete: yeni alanlar (geriye uyumlu — eski alanlar optional).
  readonly deletedAliases?: number;
  readonly deletedRenewalJobs?: number;
  readonly deletedSessions?: number;
  readonly deletedInboxMessages?: number;
  readonly deletedInventoryRows?: number;
  readonly deletedCredentialBlobs?: number;
  readonly credentialRefsCollected?: number;
  /**
   * Present only when the cascade partially failed. The server deletes
   * table-by-table WITHOUT a transaction, so a failure mid-way leaves orphan
   * rows behind and the audit entry is written with outcome `partial`.
   */
  readonly failures?: ReadonlyArray<{ readonly table: string; readonly error: string }>;
  // Legacy soft-delete alanları — hard-delete sunucusu bunları ASLA döndürmez.
  // Okumayın; `deletedAliases` kullanın (eski mesaj bu yüzden "undefined" yazıyordu).
  readonly disabledAliases?: number;
  readonly protectedLeasedAliases?: number;
}

export function postMailcomExportConfirmDelete(
  options: ApiClientOptions,
  exportId: string,
  body: { confirmationToken: string },
  providerId: string = 'mailcom',
): Promise<ApiResult<MailcomExportConfirmResponse>> {
  return callAdminApi(options, {
    method: 'POST',
    path: `/admin/providers/${encodeURIComponent(providerId)}/parents/exports/${encodeURIComponent(exportId)}/confirm-delete`,
    body,
    idempotencyKey: freshIdempotencyKey(`${providerId}-export-confirm-${exportId}`),
  });
}

// R-D214 — operator override to zero out a parent's cooldown_until and
// flip status='active'. 409 when the row has no active cooldown.
export interface MailcomClearCooldownResponse {
  readonly accountId: string;
  readonly outcome: 'cleared';
  readonly clearedAt: string;
}

export function postMailcomClearCooldown(
  options: ApiClientOptions,
  accountId: string,
  body: { reason: string },
  providerId: string = 'mailcom',
): Promise<ApiResult<MailcomClearCooldownResponse>> {
  return callAdminApi(options, {
    method: 'POST',
    path: `/admin/providers/${encodeURIComponent(providerId)}/parents/${encodeURIComponent(accountId)}/clear-cooldown`,
    body,
    idempotencyKey: freshIdempotencyKey(`${providerId}-clear-cooldown-${accountId}`),
  });
}

// 2026-09-22 — operator reactivation of a parent disabled at the GMX phone
// wall (status='disabled' + status_detail='phone_verification_required').
// Server flips the account back to active and returns its 'disabled' aliases
// to sale in one transaction; nothing is enqueued. 409
// {error:'not_phone_wall_disabled'} for any other row.
export interface ProviderParentReactivateResponse {
  readonly ok: true;
  readonly accountId: string;
  readonly status: 'active';
  readonly aliasesRestored: number;
}

export async function postProviderParentReactivate(
  options: ApiClientOptions,
  providerId: string,
  accountId: string,
  reason: string,
): Promise<ApiResult<ProviderParentReactivateResponse>> {
  const res = await callAdminApi<ProviderParentReactivateResponse>(options, {
    method: 'POST',
    path: `/admin/providers/${encodeURIComponent(providerId)}/parents/${encodeURIComponent(accountId)}/reactivate`,
    body: { reason },
    idempotencyKey: freshIdempotencyKey(`${providerId}-reactivate-${accountId}`),
  });
  // This route answers errors as {error:'<code>'} with no `reason`, which the
  // client would flatten to 'http_<status>'. Surface the server code instead so
  // the operator sees e.g. 'not_phone_wall_disabled'.
  if (res.kind === 'error' && res.reason === `http_${res.status}`) {
    const code = res.body?.error;
    if (typeof code === 'string' && code.length > 0) return { ...res, reason: code };
  }
  return res;
}

// ---------------------------------------------------------------------------
// Order forensics (GET /admin/orders/:id/forensics)
//
// The rental chain behind one order: which site it was bought for, which
// mailbox was leased, which PARENT account owns that mailbox, what the parent's
// health is, what money moved, and every inbox row the poller saved. The server
// also returns a one-line verdict (`diagnosis`) so the page leads with "why did
// no mail arrive" instead of making the operator derive it from raw fields.
// ---------------------------------------------------------------------------

export interface WireOrderDiagnosis {
  readonly code: string;
  readonly severity: 'ok' | 'info' | 'warn' | 'critical';
  readonly title: string;
  readonly detail: string;
}

export interface WireForensicsOutcome {
  readonly kind: string;
  readonly providerId: string | null;
  readonly failureReason: string | null;
  readonly providerMessageKey: string | null;
  readonly subject: string | null;
  readonly from: string | null;
  readonly detail: string | null;
  readonly at: string | null;
}

export interface WireOrderForensics {
  readonly order: {
    readonly orderId: string;
    readonly state: string;
    readonly actorId: string | null;
    readonly actorEmail: string | null;
    readonly correlationId: string | null;
    readonly reservationId: string | null;
    readonly siteKey: string | null;
    readonly siteLabel: string | null;
    readonly domain: string | null;
    readonly openedAt: string;
    readonly updatedAt: string;
    readonly closedAt: string | null;
    readonly outcome: WireForensicsOutcome | null;
  };
  readonly reservation: {
    readonly reservationId: string;
    readonly sku: string | null;
    readonly status: string | null;
    readonly providerId: string | null;
    readonly quantity: number | null;
    readonly leaseExpiresAt: string | null;
    readonly leaseActive: boolean;
    readonly providerAssignment: string | null;
    readonly createdAt: string | null;
  } | null;
  readonly mailbox: {
    readonly aliasId: string;
    readonly address: string | null;
    readonly domain: string | null;
    readonly status: string | null;
    readonly servedCount: number;
    readonly lastServedAt: string | null;
    readonly createdAt: string | null;
  } | null;
  readonly parent: {
    readonly accountId: string;
    readonly handle: string | null;
    readonly status: string | null;
    readonly statusDetail: string | null;
    readonly sessionState: string | null;
    readonly consecutiveErrors: number;
    readonly cooldownUntil: string | null;
    readonly suspendedReason: string | null;
    readonly lastSessionCheckedAt: string | null;
    readonly lastSuccessfulFetchAt: string | null;
    readonly lastAllocatedAt: string | null;
  } | null;
  readonly charge: {
    readonly currency: string;
    readonly chargedMicro: number;
    readonly refundedMicro: number;
    readonly netMicro: number;
    readonly chargedAt: string | null;
    readonly refundedAt: string | null;
  };
  readonly messages: readonly {
    readonly id: string;
    readonly receivedAt: string;
    readonly subject: string | null;
    readonly from: string | null;
    readonly hasCode: boolean;
    readonly codeConfidence: number | null;
    readonly codeSource: string | null;
  }[];
  readonly timeline: readonly {
    readonly eventId: string;
    readonly providerId: string;
    readonly outcomeKind: string;
    readonly failureReason: string | null;
    readonly providerMessageKey: string | null;
    readonly subject: string | null;
    readonly from: string | null;
    readonly detail: string | null;
    readonly recordedAt: string;
  }[];
  readonly polling: {
    readonly status: string;
    readonly matchedCodePresent: boolean | null;
    readonly updatedAt: string;
  } | null;
  readonly diagnosis: WireOrderDiagnosis;
  readonly observedAt: string;
}

export interface AdminOrderForensicsResponse {
  readonly status: 'ok';
  readonly data: WireOrderForensics;
  readonly correlationId: string;
}

export function getAdminOrderForensics(
  options: ApiClientOptions,
  orderId: string,
): Promise<ApiResult<AdminOrderForensicsResponse>> {
  return callAdminApi(options, {
    method: 'GET',
    path: `/admin/orders/${encodeURIComponent(orderId)}/forensics`,
  });
}

export { resolveBaseUrl };
